import type { flows } from '@repletoryreach/sdk-api';
import type { JsonObject } from '../flows/builder/mapper';

/**
 * Small rules the API wizard shares (19 October 2026). The server reads the documentation and drafts the sources;
 * these only turn what it gave into the forms a person reviews.
 */

/** A name a flow can use, as the server makes it: `data.slots[].placesLeft` as `places_left`. */
export function slug(text: string): string {
  const last = text.includes('.') ? text.split('.').pop() ?? text : text;
  let s = last.replace(/\[\]/g, '').replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
  if (s.length === 0 || !/^[a-z]/.test(s)) s = `f_${s}`;
  return s.length > 40 ? s.slice(0, 40).replace(/_+$/, '') : s;
}

/** `data.slots[].placesLeft` as "Places left". */
export function label(path: string): string {
  const last = (path.split('.').pop() ?? path).replace(/\[\]/g, '');
  const words = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.length === 0 ? path : words[0].toUpperCase() + words.slice(1);
}

/** What an endpoint is called in a list: its summary, else its method and path. */
export const endpointTitle = (e: flows.ApiDocEndpoint): string => (e.summary?.trim() ? e.summary.trim() : `${e.method} ${e.path}`);

/** The connection the wizard starts from: what the documentation says, or the saved connection being added to. */
export function startingConnection(doc: flows.ApiDoc | null, saved?: flows.ApiConnection | null): { name: string; base_url: string; auth: JsonObject; headers?: Record<string, string> } {
  if (saved) return { name: saved.name, base_url: saved.base_url, auth: { ...(saved.auth as JsonObject) }, headers: saved.headers };
  const auth: JsonObject = { type: doc?.auth.type ?? 'none' };
  if (doc?.auth.key_name) auth.key_name = doc.auth.key_name;
  if (doc?.auth.in) auth.in = doc.auth.in;
  if (doc?.auth.token_url) auth.token_url = doc.auth.token_url;
  if (doc?.auth.scope) auth.scope = doc.auth.scope;
  return { name: doc?.title?.trim() || '', base_url: doc?.base_url ?? '', auth };
}

/** The connection as the server takes it. A sign-in of type none is sent as such, so a change to none is kept. */
export function connectionInput(c: { name: string; base_url: string; auth: JsonObject; headers?: Record<string, string> }, doc: flows.ApiDoc | null): flows.ApiConnectionInput {
  return {
    name: c.name.trim(),
    base_url: c.base_url.trim(),
    auth: { ...(c.auth.type ? c.auth : { type: 'none' }) } as flows.ApiConnectionInput['auth'],
    headers: c.headers,
    docs_kind: doc?.kind,
    docs_title: doc?.title ?? null
  };
}

/** The endpoint tried first: one that reads and needs nothing without an example, else the first that reads. */
export function firstToTry(doc: flows.ApiDoc): number {
  const reads = doc.endpoints.map((e, i) => ({ e, i })).filter(({ e }) => e.kind === 'read');
  const easy = reads.find(({ e }) => e.parameters.every((p) => !p.required || p.example));
  return (easy ?? reads[0])?.i ?? -1;
}

/** An endpoint that does something, saved for flows' Web API steps. */
export function actionFrom(e: flows.ApiDocEndpoint, name?: string): flows.ApiActionInput {
  return {
    name: (name ?? endpointTitle(e)).slice(0, 120),
    description: e.description ?? null,
    method: e.method.toUpperCase(),
    path: e.path,
    parameters: e.parameters,
    body: e.body_example ?? undefined,
    fields: e.fields.map((f) => ({ path: f.path, key: slug(f.path), label: label(f.path), description: f.description ?? null }))
  };
}

/** A data point being reviewed: every field of the endpoint, the chosen ones ticked. */
export interface PointRow { path: string; key: string; label: string; description?: string; on: boolean }

export function pointRows(draft: flows.ApiDraft): PointRow[] {
  const chosen = draft.source.response_paths ?? [];
  const rows: PointRow[] = chosen.map((p) => ({ path: p.path, key: p.key ?? slug(p.path), label: p.label ?? label(p.path), description: p.description, on: true }));
  for (const path of draft.all_fields)
    if (!rows.some((r) => r.path === path)) rows.push({ path, key: slug(path), label: label(path), on: false });
  // Each key once: a second field with the same last name gets a number.
  const used = new Set<string>();
  for (const r of rows) {
    let k = r.key;
    for (let n = 2; used.has(k); n++) k = `${r.key}_${n}`;
    used.add(k);
    r.key = k;
  }
  return rows;
}

export function pointsInput(rows: PointRow[]): flows.LiveSourcePath[] {
  return rows.filter((r) => r.on).map((r) => ({ path: r.path, key: r.key, label: r.label.trim() || label(r.path), ...(r.description ? { description: r.description } : {}) }));
}

/** Text files are read in the browser; a PDF goes through the media library so its text can be taken out. */
export const isPdf = (file: File): boolean => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

export const DOC_ACCEPT = ['.json', '.yaml', '.yml', '.md', '.markdown', '.txt', '.pdf', 'application/json', 'application/x-yaml', 'text/yaml', 'text/markdown', 'text/plain', 'application/pdf'];
